import { useEffect, useMemo, useState } from "react";

import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
  TableHeading,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar } from "@fluentui/react-components";

import { CompanyUserAccountType, TeamMember } from "@/queries/company/team";
import { useRemoveTeamMember } from "@/mutations/company/team/remove-team-member";
import { useUpdateTeamMember } from "@/mutations/company/team/update-team-member";
import { useResetTeamMemberPassword } from "@/mutations/company/team/reset-team-member-password";
import { AddTeamMemberDialog } from "@/components/dialogs/company/team/add-team";

const SORTABLE_FIELDS = ["name", "email", "accountType"] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const USERS_PER_PAGE = 10;

const TeamTable = ({
  companyId,
  companyTeam,
}: {
  companyId: string;
  companyTeam?: TeamMember[];
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("name");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const sortedTeam: TeamMember[] | undefined = useMemo(() => {
    if (!companyTeam) {
      return;
    }

    switch (sortField) {
      case "name":
        return [...companyTeam].sort(
          (a, b) => (a.name ?? "").localeCompare(b.name ?? "") * sortDirection,
        );
      case "email":
        return [...companyTeam].sort(
          (a, b) =>
            (a.emails[0] ?? "").localeCompare(b.emails[0] ?? "") *
            sortDirection,
        );
      case "accountType":
        return [...companyTeam].sort(
          (a, b) =>
            (a.accountType ?? "").localeCompare(b.accountType ?? "") *
            sortDirection,
        );
    }
  }, [companyTeam, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedTeam) {
      return 1;
    }

    return Math.ceil(sortedTeam.length / USERS_PER_PAGE);
  }, [sortedTeam]);

  const currentPageCompanyTeam = useMemo(
    () => sortedTeam?.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE),
    [sortedTeam, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages && page !== 1) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableHeading>
            <div className="flex-1 flex flex-col items-center justify-center gap-[2px]">
              <div className="flex gap-2 items-center self-stretch">
                <p className="text-lg font-semibold text-[#0C0D0D]">
                  Company Team
                </p>
                {companyTeam ? (
                  <div className="px-1.5 py-0.5 border border-[#D5D7DA] rounded-md shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                    <p className="text-xs font-medium text-[#414651] text-center">
                      {companyTeam.length === 1
                        ? "1 team member"
                        : `${companyTeam.length} team members`}
                    </p>
                  </div>
                ) : null}
              </div>
              <p className="self-stretch text-sm text-[#5c6060] overflow-hidden text-ellipsis">
                Manage your team members and their account permissions here.
              </p>
            </div>
            <AddTeamMemberDialog
              trigger={<Button variant="outline">Add team member</Button>}
              companyId={companyId}
            />
          </TableHeading>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("name")}
                  sortDirection={
                    sortField === "name" ? sortDirection : undefined
                  }
                >
                  Name
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("email")}
                  sortDirection={
                    sortField === "email" ? sortDirection : undefined
                  }
                >
                  Email address
                </TableRowItem>
                <TableRowItem heading>Attio</TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("accountType")}
                  sortDirection={
                    sortField === "accountType" ? sortDirection : undefined
                  }
                >
                  Permissions
                </TableRowItem>
                <TableRowItem heading button></TableRowItem>
                <TableRowItem heading button></TableRowItem>
              </TableRow>
            </thead>
            <tbody>
              {currentPageCompanyTeam?.map((user) => (
                <Row
                  key={`team-member-${user.id}`}
                  user={user}
                  companyId={companyId}
                />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const Row = ({ user, companyId }: { user: TeamMember; companyId: string }) => {
  const { removeTeamMember, isRemovingTeamMember } =
    useRemoveTeamMember(companyId);
  const { updateTeamMember, isUpdatingTeamMember } =
    useUpdateTeamMember(companyId);
  const { resetTeamMemberPassword, isResettingTeamMemberPassword } =
    useResetTeamMemberPassword(companyId);

  return (
    <TableRow>
      <TableRowItem>
        <div className="flex flex-row gap-3 items-center">
          <Avatar
            image={{ src: user.profilePictureLink }}
            name={user.name}
            size={40}
          />
          <span className="text-[#0C0D0D] text-sm font-medium">
            {user.name}
          </span>
        </div>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">{user.emails[0]}</span>
      </TableRowItem>
      <TableRowItem>
        {Boolean(user.attioId) && (
          <a
            href={`https://app.attio.com/venturous/person/${user.attioId}/activity`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-[#646cff] font-medium hover:underline"
          >
            Link
          </a>
        )}
      </TableRowItem>
      <TableRowItem>
        <Select
          name="accountType"
          value={user.accountType}
          disabled={
            isRemovingTeamMember ||
            isUpdatingTeamMember ||
            isResettingTeamMemberPassword
          }
          onValueChange={(val: CompanyUserAccountType) =>
            updateTeamMember({ userId: user.userId, accountType: val })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Admin">
              <div className="flex flex-row items-center gap-1">
                <div className="h-2 w-2 bg-[#d23b15] rounded-full" />
                Admin
              </div>
            </SelectItem>
            <SelectItem value="Collaborator">
              <div className="flex flex-row items-center gap-1">
                <div className="h-2 w-2 bg-[#0d7163] rounded-full" />
                Collaborator
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableRowItem>

      <TableRowItem>
        <Button
          variant="outline"
          disabled={
            isRemovingTeamMember ||
            isUpdatingTeamMember ||
            isResettingTeamMemberPassword
          }
          onClick={() => resetTeamMemberPassword(user.userId)}
        >
          Reset Password
        </Button>
      </TableRowItem>
      <TableRowItem>
        <Button
          variant="ghost"
          disabled={
            isRemovingTeamMember ||
            isUpdatingTeamMember ||
            isResettingTeamMemberPassword
          }
          onClick={() => removeTeamMember(user.userId)}
        >
          <Trash2 className="!stroke-[#A4A7AE]" />
        </Button>
      </TableRowItem>
    </TableRow>
  );
};

export default TeamTable;
